import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const OurLogo = () => {
  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Header */}
      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Our Logo
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            A visual manifesto of what <span className="text-[#8B1818] font-semibold">Lex</span>DoRight stands for
          </p>
        </div>
      </div>

      {/* Logo Display */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center mb-12">
            <div className="bg-[#8B1818] p-12 rounded-3xl shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_56e2863f-106a-4252-be25-54a4d7e6e68e/artifacts/8hjqi8mu_LDR_Logo.png" 
                alt="LexDoRight Logo" 
                className="h-48 w-auto"
              />
            </div>
          </div>
          
          <div className="text-center mb-12">
            <p className="text-lg text-slate-700 leading-relaxed">
              The logo serves as a visual manifesto of <span className="text-[#8B1818] font-semibold">Lex</span>DoRight's core identity, capturing in a single image how law, integrity, and everyday judgment come together in practice.
            </p>
          </div>
        </div>
      </section>

      {/* Logo Meaning */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Pillar and Capital */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
              <div className="h-16 w-16 bg-[#8B1818]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="h-8 w-8 text-[#8B1818]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4 text-center" style={{fontFamily: 'Playfair Display, serif'}}>
                Pillar and Capital
              </h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start">
                  <span className="text-[#FDB913] mr-2">•</span>
                  <span>The column with a classical capital suggests law, institutions, and enduring principles, evoking courts, legislatures, and good governance structures.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#FDB913] mr-2">•</span>
                  <span>It signals stability and continuity, implying that <span className="text-[#8B1818] font-semibold">Lex</span>DoRight is grounded on established legal and ethical foundations rather than passing trends.</span>
                </li>
              </ul>
            </div>

            {/* Cat Figure */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
              <div className="h-16 w-16 bg-[#8B1818]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="h-8 w-8 text-[#8B1818]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4 text-center" style={{fontFamily: 'Playfair Display, serif'}}>
                Cat Figure
              </h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start">
                  <span className="text-[#FDB913] mr-2">•</span>
                  <span>The cat inside the pillar can symbolize alertness, discernment, and quiet vigilance, reflecting the careful reading, checking, and judgment that legal and governance work require.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#FDB913] mr-2">•</span>
                  <span>Cats are also associated with independence and curiosity, hinting at professionals who think critically, question assumptions, and refuse to cut corners.</span>
                </li>
              </ul>
            </div>

            {/* Overall Composition */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
              <div className="h-16 w-16 bg-[#8B1818]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="h-8 w-8 text-[#8B1818]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4 text-center" style={{fontFamily: 'Playfair Display, serif'}}>
                Overall Composition
              </h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start">
                  <span className="text-[#FDB913] mr-2">•</span>
                  <span>The cat framed by the column suggests humans working "inside" the structure of law, using it as both protection and guidance rather than a mere constraint.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#FDB913] mr-2">•</span>
                  <span>The warm gold against the deep background can represent integrity, wisdom, and earned authority, aligning with a brand that helps people not just know the rules but "do right" with confidence.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
            Learn More About Us
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Discover our mission and how we're making it easier to do what is right.
          </p>
          <Link to="/who-we-are">
            <Button className="bg-[#8B1818] text-white hover:bg-[#701313] rounded-full px-8 py-6 text-lg font-semibold">
              Who We Are
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default OurLogo;
